/**
 * Sidebar native-language picker.
 *
 * The native HTML <select> can't render images inside <option>, so we build
 * a small custom listbox. Each row shows: english name | flag (lipis/flag-icons).
 */

import { LANGUAGES, type LanguageOption } from '../engine/languages';
import { getNativeLanguage, setNativeLanguage } from '../engine/language';

export function mountLanguagePicker(host: HTMLElement): void {
  const sorted = [...LANGUAGES].sort((a, b) => a.english.localeCompare(b.english));

  host.classList.add('lang-combo');
  host.innerHTML = '';

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'lang-combo-button';
  button.setAttribute('aria-haspopup', 'listbox');
  button.setAttribute('aria-expanded', 'false');

  const list = document.createElement('ul');
  list.className = 'lang-combo-list';
  list.setAttribute('role', 'listbox');
  list.hidden = true;

  for (const lang of sorted) {
    const item = document.createElement('li');
    item.className = 'lang-combo-item';
    item.setAttribute('role', 'option');
    item.dataset.code = lang.code;
    item.innerHTML = renderRow(lang);
    item.addEventListener('click', () => {
      select(lang.code);
      close();
    });
    list.append(item);
  }

  host.append(button, list);

  const open = () => {
    list.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    const sel = list.querySelector<HTMLElement>(`[data-code="${getNativeLanguage()}"]`);
    sel?.scrollIntoView({ block: 'center' });
  };
  const close = () => {
    list.hidden = true;
    button.setAttribute('aria-expanded', 'false');
  };

  button.addEventListener('click', () => {
    if (list.hidden) open();
    else close();
  });

  document.addEventListener('mousedown', (e) => {
    if (!host.contains(e.target as Node)) close();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') close();
  });

  function select(code: string): void {
    setNativeLanguage(code);
    paintButton();
    list.querySelectorAll<HTMLElement>('.lang-combo-item').forEach((el) => {
      el.setAttribute('aria-selected', String(el.dataset.code === code));
    });
  }

  function paintButton(): void {
    const lang = LANGUAGES.find((l) => l.code === getNativeLanguage()) ?? LANGUAGES[0]!;
    button.innerHTML = `
      <span class="lang-combo-label">${escapeHtml(lang.english)}</span>
      <span class="lang-combo-sep" aria-hidden="true">|</span>
      <span class="fi fi-${lang.country} lang-flag" aria-hidden="true"></span>
      <svg class="lang-combo-chev" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.75" fill="none" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  paintButton();
  select(getNativeLanguage());
}

function renderRow(lang: LanguageOption): string {
  return `
    <span class="lang-combo-label">${escapeHtml(lang.english)}</span>
    <span class="lang-combo-sep" aria-hidden="true">|</span>
    <span class="fi fi-${lang.country} lang-flag" aria-hidden="true"></span>
  `;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]!);
}
